/**
 * NOS-270: split Publish button + dropdown for the builder top bar, extracted
 * from WebsiteBuilderPage. Owns its open/close and schedule-input state;
 * actions come in via props. Blog mode when `blog` is set, page mode
 * otherwise (campaigns publish inside the campaign editor and never render
 * this menu).
 */
import { useEffect, useRef, useState } from "react"

const MENU_ITEM: React.CSSProperties = {
  width: "100%",
  padding: "9px 14px",
  background: "transparent",
  border: "none",
  color: "rgba(255,255,255,0.7)",
  fontSize: 12,
  cursor: "pointer",
  textAlign: "left",
}

function MenuItem({
  danger = false,
  onClick,
  children,
}: {
  danger?: boolean
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{ ...MENU_ITEM, ...(danger ? { color: "#ef4444" } : {}) }}
    >
      {children}
    </button>
  )
}

const MENU_DIVIDER = <div style={{ height: 1, background: "rgba(255,255,255,0.06)" }} />

export interface PublishMenuBlogActions {
  status: "draft" | "published" | "scheduled"
  isPublished: boolean
  onPublish: () => void
  /** Receives the datetime-local input value */
  onSchedule: (dateLocal: string) => void
  onConvertToDraft: () => void
  onDelete: () => void
}

export interface PublishMenuPageActions {
  isPublished: boolean
  onPublish: () => void
  onOpenSettings: () => void
  onConvertToDraft: () => void
  /** null hides the item (the homepage cannot be deleted) */
  onDelete: ((e: React.MouseEvent) => void) | null
}

export function PublishMenu({
  primaryColor,
  blog,
  page,
}: {
  primaryColor: string
  blog?: PublishMenuBlogActions | null
  page: PublishMenuPageActions
}) {
  const [open, setOpen] = useState(false)
  const [showScheduleInput, setShowScheduleInput] = useState(false)
  const [scheduleDate, setScheduleDate] = useState("")
  const rootRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
        setShowScheduleInput(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [open])

  const close = () => {
    setOpen(false)
    setShowScheduleInput(false)
  }

  const isPublished = blog ? blog.isPublished : page.isPublished

  return (
    <div style={{ display: "flex", position: "relative" }} ref={rootRef}>
      <button
        type="button"
        onClick={() => {
          if (blog) close()
          ;(blog ? blog.onPublish : page.onPublish)()
        }}
        style={{
          padding: "6px 14px",
          background: primaryColor,
          border: "none",
          borderRadius: "8px 0 0 8px",
          color: "#fff",
          fontSize: 11,
          fontWeight: 500,
          cursor: "pointer",
        }}
      >
        {isPublished ? "Published ✓" : "Publish"}
      </button>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          padding: "6px 8px",
          background: primaryColor,
          border: "none",
          borderLeft: "1px solid rgba(255,255,255,0.2)",
          borderRadius: "0 8px 8px 0",
          color: "#fff",
          fontSize: 10,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
        }}
      >
        ▾
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            right: 0,
            background: "rgba(16,16,22,0.98)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            border: "1px solid rgba(255,255,255,0.09)",
            borderRadius: 10,
            boxShadow: "0 12px 40px rgba(0,0,0,0.5)",
            zIndex: 300,
            overflow: "hidden",
            minWidth: 160,
          }}
        >
          {blog ? (
            <>
              {blog.status === "draft" ? (
                <>
                  <MenuItem onClick={() => { close(); blog.onPublish() }}>
                    Publish Now
                  </MenuItem>
                  <MenuItem onClick={() => setShowScheduleInput((v) => !v)}>
                    Schedule…
                  </MenuItem>
                  {showScheduleInput && (
                    <div
                      style={{
                        padding: "8px 14px 12px",
                        borderTop: "1px solid rgba(255,255,255,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <input
                        type="datetime-local"
                        value={scheduleDate}
                        onChange={(e) => setScheduleDate(e.target.value)}
                        style={{
                          width: "100%",
                          background: "rgba(255,255,255,0.06)",
                          border: "1px solid rgba(255,255,255,0.1)",
                          borderRadius: 6,
                          padding: "5px 8px",
                          color: "#fff",
                          fontSize: 11,
                          outline: "none",
                          boxSizing: "border-box",
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (!scheduleDate) return
                          close()
                          blog.onSchedule(scheduleDate)
                          setScheduleDate("")
                        }}
                        disabled={!scheduleDate}
                        style={{
                          padding: "5px 10px",
                          background: scheduleDate ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.04)",
                          border: "none",
                          borderRadius: 6,
                          color: scheduleDate ? "#fff" : "rgba(255,255,255,0.3)",
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: scheduleDate ? "pointer" : "not-allowed",
                        }}
                      >
                        Set Schedule
                      </button>
                    </div>
                  )}
                  {MENU_DIVIDER}
                </>
              ) : (
                <MenuItem onClick={() => { close(); blog.onConvertToDraft() }}>
                  Convert to Draft
                </MenuItem>
              )}
              <MenuItem danger onClick={() => { close(); blog.onDelete() }}>
                Delete post
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem onClick={() => { close(); page.onOpenSettings() }}>
                Page settings
              </MenuItem>
              {MENU_DIVIDER}
              {page.isPublished && (
                <MenuItem onClick={() => { close(); page.onConvertToDraft() }}>
                  Convert to Draft
                </MenuItem>
              )}
              {page.onDelete && (
                <MenuItem danger onClick={(e) => { close(); page.onDelete!(e) }}>
                  Delete page
                </MenuItem>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
